import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Admin } from '../utils/api';

// Markets shown in the admin UI; order determines display order.
const MARKETS = [
  { code: 'uk',  label: 'United Kingdom' },
  { code: 'us',  label: 'United States' },
  { code: 'th',  label: 'Thailand' },
  { code: 'ksa', label: 'Saudi Arabia' },
];

// Display labels for TH verticals (slug → label)
const TH_LABELS = { food: 'Food Delivery', ridehailing: 'Ride Hailing' };

const TYPE_META = {
  eureka: { label: 'Eureka Team',                color: '#1DB89A' },
  client: { label: 'Clients',                    color: '#6366f1' },
  other:  { label: 'Other accounts (no access)', color: '#9CA3AF' },
};

function fmtTs(ms) {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function catLabel(countryCode, cat) {
  if (countryCode === 'th') return TH_LABELS[cat] || cat;
  return cat;
}

function Chip({ children, color = '#1DB89A' }) {
  return (
    <span style={{
      display: 'inline-block', fontSize: 11, fontWeight: 600,
      color, background: `${color}14`, border: `1px solid ${color}33`,
      borderRadius: 100, padding: '2px 9px', marginRight: 4, marginBottom: 2,
    }}>{children}</span>
  );
}

export default function AdminView() {
  const { user, logout } = useAuth();
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [busy,    setBusy]    = useState(false);

  // Per-country available categories fetched from backend
  const [availableByCountry, setAvailableByCountry] = useState({});

  // Grant form — countryCategories: { [countryCode]: string[] }
  // A country code key present → that market is granted.
  // Empty array for a code → all categories for that market.
  // Non-empty array → only those specific categories.
  const [email,              setEmail]              = useState('');
  const [countryCategories,  setCountryCategories]  = useState({});
  const [granted,            setGranted]            = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setRows(await Admin.accessUsers()); }
    catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    Admin.countryCategories().then(data => {
      const item = Array.isArray(data) ? data[0] : data;
      setAvailableByCountry(item || {});
    }).catch(() => {});
  }, []);

  // Toggle a country on/off
  const toggleCountry = (code) => {
    setCountryCategories(prev => {
      if (code in prev) {
        const next = { ...prev };
        delete next[code];
        return next;
      }
      return { ...prev, [code]: [] }; // empty = all categories
    });
  };

  // Toggle a category within a country
  const toggleCategory = (countryCode, cat) => {
    setCountryCategories(prev => {
      const current = prev[countryCode] || [];
      const next = current.includes(cat)
        ? current.filter(c => c !== cat)
        : [...current, cat];
      return { ...prev, [countryCode]: next };
    });
  };

  const handleGrant = async (e) => {
    e.preventDefault();
    setBusy(true); setError(''); setGranted(null);
    try {
      const result = await Admin.grantAccess({
        email: email.trim(),
        country_categories: countryCategories,
      });
      setGranted(result);
      setEmail(''); setCountryCategories({});
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  const handleRevoke = async (row) => {
    const msg = row.type === 'eureka'
      ? `Remove restrictions for ${row.email}? (Eureka accounts keep full access)`
      : `Revoke Omni Growth access for ${row.email}?`;
    if (!window.confirm(msg)) return;
    setBusy(true); setError('');
    try { await Admin.revokeAccess(row.email); await load(); }
    catch (err) { setError(err?.response?.data?.detail || err.message); }
    finally { setBusy(false); }
  };

  const cardStyle = {
    background: 'var(--bg-card, #fff)',
    border: '1px solid rgba(29,184,154,0.20)',
    borderRadius: 18,
    boxShadow: '0 10px 36px rgba(29,120,102,0.08)',
    padding: 24,
    marginBottom: 20,
  };

  const hasAnyAvailable = Object.values(availableByCountry).some(cats => cats?.length > 0);

  return (
    <div style={{
      minHeight: '100vh',
      background:
        'radial-gradient(ellipse 60% 50% at 80% 0%, rgba(29,184,154,0.10) 0%, transparent 70%), var(--bg, #f0faf8)',
      fontFamily: 'Inter, -apple-system, sans-serif',
      color: 'var(--text-primary, #111827)',
    }}>
      {/* Topbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '14px 28px', borderBottom: '1px solid rgba(29,184,154,0.15)',
        background: 'rgba(255,255,255,0.65)', backdropFilter: 'blur(24px) saturate(180%)',
        WebkitBackdropFilter: 'blur(24px) saturate(180%)',
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <img src="/eureka-logo.png" alt="Eureka AI" style={{ height: 30 }} />
        <div style={{ fontSize: 15, fontWeight: 700 }}>Access Management</div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: 'var(--text-muted, #9CA3AF)' }}>{user?.email}</span>
        <a href="/" style={{ fontSize: 13, fontWeight: 600, color: '#1DB89A', textDecoration: 'none' }}>← Back to app</a>
        <button onClick={logout} style={{
          fontSize: 12, fontWeight: 600, color: 'var(--text-muted, #6B7280)',
          background: 'none', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 100,
          padding: '4px 12px', cursor: 'pointer',
        }}>Sign out</button>
      </div>

      <div style={{ maxWidth: 960, margin: '0 auto', padding: '28px 20px 60px' }}>

        {/* Grant access */}
        <div style={cardStyle}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Grant access</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted, #6B7280)', marginBottom: 20, lineHeight: 1.5 }}>
            Client emails get a Firebase account (with a one-time temp password) and an allowlist entry.
            Eureka emails only get restrictions — leave all markets unticked for full access.
          </div>
          <form onSubmit={handleGrant}>
            <input
              type="email" required placeholder="person@company.com"
              value={email} onChange={e => setEmail(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '9px 14px', fontSize: 14, marginBottom: 20,
                border: '1px solid rgba(29,184,154,0.25)', borderRadius: 10,
                background: 'rgba(255,255,255,0.7)', outline: 'none',
              }}
            />

            {/* Country → Categories matrix */}
            {hasAnyAvailable && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted, #9CA3AF)', marginBottom: 12 }}>
                  Markets &amp; Categories <span style={{ fontWeight: 400 }}>(leave all blank = full access)</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {MARKETS.map(m => {
                    const available = availableByCountry[m.code] || [];
                    if (!available.length) return null;
                    const isGranted = m.code in countryCategories;
                    const selectedCats = countryCategories[m.code] || [];
                    return (
                      <div key={m.code} style={{
                        border: `1px solid ${isGranted ? 'rgba(29,184,154,0.35)' : 'rgba(0,0,0,0.08)'}`,
                        borderRadius: 10, overflow: 'hidden',
                        background: isGranted ? 'rgba(29,184,154,0.03)' : 'transparent',
                      }}>
                        {/* Country row */}
                        <label style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '9px 14px', cursor: 'pointer',
                          fontWeight: 600, fontSize: 13,
                          color: isGranted ? '#1DB89A' : 'var(--text-primary, #374151)',
                        }}>
                          <input
                            type="checkbox"
                            checked={isGranted}
                            onChange={() => toggleCountry(m.code)}
                            style={{ accentColor: '#1DB89A', width: 15, height: 15 }}
                          />
                          {m.label}
                          {isGranted && selectedCats.length === 0 && (
                            <span style={{ fontSize: 11, fontWeight: 400, color: '#1DB89A', marginLeft: 4 }}>all categories</span>
                          )}
                        </label>

                        {/* Category row — shown when market is ticked */}
                        {isGranted && (
                          <div style={{
                            display: 'flex', flexWrap: 'wrap', gap: 8,
                            padding: '8px 14px 12px 38px',
                            borderTop: '1px solid rgba(29,184,154,0.12)',
                          }}>
                            {available.map(cat => {
                              const label = catLabel(m.code, cat);
                              const checked = selectedCats.includes(cat);
                              return (
                                <label key={cat} style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 5,
                                  fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                                  color: checked ? '#6366f1' : 'var(--text-muted, #6B7280)',
                                }}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleCategory(m.code, cat)}
                                    style={{ accentColor: '#6366f1' }}
                                  />
                                  {label}
                                </label>
                              );
                            })}
                            {selectedCats.length === 0 && (
                              <span style={{ fontSize: 11.5, color: 'var(--text-muted, #9CA3AF)', alignSelf: 'center' }}>
                                tick categories to restrict, or leave blank for all
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <button type="submit" disabled={busy} style={{
              fontSize: 13, fontWeight: 700, color: '#fff',
              background: 'linear-gradient(145deg, rgba(29,184,154,0.9) 0%, rgba(14,154,126,0.8) 100%)',
              border: '1px solid rgba(255,255,255,0.28)', borderRadius: 100,
              padding: '9px 22px', cursor: busy ? 'wait' : 'pointer',
              boxShadow: '0 6px 18px rgba(29,184,154,0.30), inset 0 1px 0 rgba(255,255,255,0.35)',
            }}>
              {busy ? 'Working…' : 'Grant access'}
            </button>
          </form>

          {granted && (
            <div style={{
              marginTop: 14, padding: '10px 14px', fontSize: 13, lineHeight: 1.6,
              background: 'rgba(29,184,154,0.08)', border: '1px solid rgba(29,184,154,0.30)',
              borderRadius: 10,
            }}>
              ✓ Access granted to <b>{granted.email}</b>
              {granted.temp_password && (
                <div>
                  Temp password (shown once — share it securely):{' '}
                  <code style={{ fontWeight: 700, userSelect: 'all' }}>{granted.temp_password}</code>
                  {' '}· they'll be asked to change it on first login
                </div>
              )}
            </div>
          )}
          {error && (
            <div style={{
              marginTop: 14, padding: '10px 14px', fontSize: 13,
              background: '#FEF2F2', border: '1px solid #FEE2E2', borderRadius: 10, color: '#EF4444',
            }}>{String(error)}</div>
          )}
        </div>

        {/* Account lists */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted, #9CA3AF)' }}>Loading accounts…</div>
        ) : ['eureka', 'client', 'other'].map(type => {
          const group = rows.filter(r => r.type === type);
          if (!group.length) return null;
          const meta = TYPE_META[type];
          return (
            <div key={type} style={cardStyle}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: meta.color }}>
                {meta.label} <span style={{ fontWeight: 500, color: 'var(--text-muted, #9CA3AF)' }}>· {group.length}</span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--text-muted, #9CA3AF)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    <th style={{ padding: '6px 8px' }}>Email</th>
                    <th style={{ padding: '6px 8px' }}>Market Access</th>
                    <th style={{ padding: '6px 8px' }}>Last sign-in</th>
                    <th style={{ padding: '6px 8px' }} />
                  </tr>
                </thead>
                <tbody>
                  {group.map(r => {
                    const cc = r.country_categories || {};
                    const countries = Object.keys(cc);
                    return (
                      <tr key={r.email} style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                        <td style={{ padding: '8px' }}>
                          {r.email}
                          {r.must_change_password && <span title="Must change password on next login"> 🔑</span>}
                          {r.disabled && <Chip color="#EF4444">disabled</Chip>}
                        </td>
                        <td style={{ padding: '8px', lineHeight: 1.8 }}>
                          {countries.length === 0
                            ? (r.has_access
                                ? <span style={{ color: 'var(--text-muted, #9CA3AF)' }}>All markets · All categories</span>
                                : '—')
                            : countries.map(code => {
                                const mkt = MARKETS.find(m => m.code === code);
                                const cats = cc[code] || [];
                                return (
                                  <div key={code} style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                                    <Chip color="#1DB89A">{mkt?.label || code}</Chip>
                                    {cats.length === 0
                                      ? <span style={{ fontSize: 11, color: 'var(--text-muted, #9CA3AF)' }}>all categories</span>
                                      : cats.map(c => <Chip key={c} color="#6366f1">{catLabel(code, c)}</Chip>)
                                    }
                                  </div>
                                );
                              })
                          }
                        </td>
                        <td style={{ padding: '8px', whiteSpace: 'nowrap', color: 'var(--text-muted, #6B7280)' }}>
                          {fmtTs(r.last_sign_in)}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>
                          {type !== 'other' && (
                            <button onClick={() => handleRevoke(r)} disabled={busy} style={{
                              fontSize: 11.5, fontWeight: 600, color: '#EF4444',
                              background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.25)',
                              borderRadius: 100, padding: '3px 12px', cursor: 'pointer',
                            }}>
                              {type === 'eureka' ? 'Clear limits' : 'Revoke'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}

        <div style={{ fontSize: 11.5, color: 'var(--text-muted, #9CA3AF)', lineHeight: 1.6 }}>
          "Other accounts" exist in this Firebase project (shared with other Eureka apps) but have no
          Omni Growth access. Revoking a client only removes their Omni Growth allowlist entry — their
          sign-in account is left untouched for other apps.
        </div>
      </div>
    </div>
  );
}
